/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from "moment";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { NIL as NIL_UUID } from "uuid";
import {
  DATE_INVALID,
  BIRTHDATE_REQUIRED,
  EMAIL_INVALID,
  EMAIL_REQUIRED,
  FIRST_NAME_REQUIRED,
  LAST_NAME_REQUIRED,
} from "./validation_strings";
import { SecuritySettingsData } from "../models/securitySettingsData";
import { userNeedsAGuardian } from "../utils/userNeedsAGuardian";

/**
 * Returns a normalized string, removing multiple whitespaces and replacing ß.
 * @param {string} inputString The string to be normalized.
 * @returns {string} The normalized string.
 */
const NormalizeString = (inputString: string): string => {
  return inputString
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/ß/g, "ss")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const CleanMatchNames = (nameSet: string, nameInput: string | undefined) => {
  if (nameInput) {
    const cleanedNameInput = NormalizeString(nameInput);
    const cleanedNameSet = NormalizeString(nameSet);
    return new RegExp(`\\b${cleanedNameSet}\\b`, "i").test(cleanedNameInput);
  }
  return false;
};

/**
 *
 * @param policy - Password policy data
 * @returns {Object} - Yup validation schemas for password and confirm password
 */
export const useDynamicYupPasswordValidations = (policy?: SecuritySettingsData) => {
  const { t } = useTranslation(["validations"]);

  const YupValidationPasswordRequired = yup
    .string()
    .required(t("password.passwordRequired"));

  const rules: yup.StringSchema[] = [
    yup.string().required(t("password.passwordRequired")),
  ];

  if (policy?.enable_min_password_length && policy?.minimum_password_length > 0) {
    rules.push(
      yup
        .string()
        .min(
          policy.minimum_password_length,
          t("password.passwordLen", { min: policy.minimum_password_length })
        )
    );
  }

  if (policy?.require_digit_in_password) {
    rules.push(yup.string().matches(/\d/, t("password.passwordDigit")));
  }

  if (policy?.require_uppercase_in_password) {
    rules.push(yup.string().matches(/[A-Z]/, t("password.passwordUpperCase")));
  }

  if (policy?.require_lowercase_in_password) {
    rules.push(yup.string().matches(/[a-z]/, t("password.passwordLowerCase")));
  }

  if (
    policy?.enable_disallowed_characters_in_password &&
    policy?.disallowed_characters_in_password
  ) {
    const escaped = policy?.disallowed_characters_in_password.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    const pattern = new RegExp(`[${escaped}]`);

    rules.push(
      yup.string().test(
        "no-disallowed-chars",
        t("password.passwordNoDisallowedChars", {
          disallowed: policy.disallowed_characters_in_password,
        }),
        (value) => typeof value === "string" && !pattern.test(value)
      )
    );
  }

  const YupValidationPassword = rules.reduce((acc, rule) => acc.concat(rule));

  const YupValidationConfirmPassword = (fieldReference: string) =>
    YupValidationPassword.oneOf(
      [yup.ref(fieldReference)],
      t("password.passwordIdentical")
    );

  return {
    YupValidationPassword,
    YupValidationConfirmPassword,
    YupValidationPasswordRequired,
  };
};

/**
 *
 * @param addressRequired - Flag indicating whether address is required or not
 * @returns {Object} - Yup validation schemas for password and confirm password
 */
export const useDynamicYupValidations = (addressRequired = true) => {
  const { t } = useTranslation(["validations"]);

  const YupValidation2FaCode = () =>
    yup
      .string()
      .required(t("auth2fa.required", { ns: "validations" }))
      .matches(/^\d{6}$/, t("auth2fa.invalidFormat", { ns: "validations" }));

  const YupValidationGuardianEmail = (
    minimumAge: number,
    email: string | undefined,
    orgaSkipGuardians = false
  ) =>
    yup.string().when("birthday", {
      is: (birthday: string) => {
        return userNeedsAGuardian(birthday, minimumAge, orgaSkipGuardians);
      },
      then: yup
        .string()
        .email(t("email.emailInvalid", { ns: "validations" }))
        .required(t("email.emailGuardianRequired", { ns: "validations" }))
        .test(
          "not-same-email",
          t("email.emailGuardianNotSame", { ns: "validations" }),
          function (value) {
            return value !== email;
          }
        ),
    });

  const YupValidationConfirmGuardianEmail = (email: string) =>
    yup.string().oneOf([yup.ref(email)], t("email.emailNoMatch"));

  const YupValidationFirstName = yup.string().required(t(FIRST_NAME_REQUIRED));

  const YupValidationMatchFirstName = (firstNameSet: string) =>
    yup
      .string()
      .required(t(FIRST_NAME_REQUIRED))
      .test("firstname-match", t("firstName.firstNameMatch"), function (nameInput) {
        return CleanMatchNames(firstNameSet, nameInput);
      });

  const YupValidationLastName = yup.string().required(t(LAST_NAME_REQUIRED));

  const YupValidationMatchLastName = (lastNameSet: string) =>
    yup
      .string()
      .required(t(LAST_NAME_REQUIRED))
      .test("lastname-match", t("lastName.lastNameMatch"), function (nameInput) {
        return CleanMatchNames(lastNameSet, nameInput);
      });

  const YupValidationEmail = yup
    .string()
    .email(t(EMAIL_INVALID))
    .required(t(EMAIL_REQUIRED));

  const YupValidationEmailOrUsername = yup
    .string()
    .required(t("emailOrUsernameRequired"));

  const YupValidationConfirmEmail = (fieldReference: string) =>
    yup
      .string()
      .oneOf([yup.ref(fieldReference)], t("email.emailNoMatch"))
      .required(t(EMAIL_REQUIRED));

  const YupValidationBirthday = yup
    .date()
    .when({
      is: (value: Date) => {
        return value !== null;
      },
      then: yup.date().typeError(t(DATE_INVALID)),
    })
    .max(new Date(), t("dates.birthdateFuture"))
    .required(t(BIRTHDATE_REQUIRED));

  const YupValidationMatchBirthday = (birthdaySet: string) =>
    yup
      .date()
      .typeError(t(DATE_INVALID))
      .required(t(BIRTHDATE_REQUIRED))
      .test("birthdays-match", t("dates.birthdateMatch"), function (birthdayInput) {
        if (birthdayInput) {
          return birthdaySet === moment(birthdayInput).format("DD.MM.YYYY");
        }
        return false;
      });

  const YupValidationDueDate = yup
    .date()
    .typeError(t("dates.dueDateInvalid"))
    .required(t("dates.dueDateRequired"));

  const YupValidationStreet = yup.string().when([], {
    is: () => addressRequired,
    then: yup.string().required(t("streetRequired")),
    otherwise: yup.string().notRequired(),
  });

  const YupValidationStreetNumber = yup.string().when([], {
    is: () => addressRequired,
    then: yup.string().required(t("streetNumberRequired")),
    otherwise: yup.string().notRequired(),
  });

  const YupValidationCity = yup.string().when([], {
    is: () => addressRequired,
    then: yup.string().required(t("cityRequired")),
    otherwise: yup.string().notRequired(),
  });

  const YupValidationZipCode = yup.string().when([], {
    is: () => addressRequired,
    then: yup
      .string()
      .matches(/^\d{5}$/, t("zipCode.zipCodeInvalid"))
      .required(t("zipCode.zipCodeRequired")),
    otherwise: yup
      .string()
      .nullable()
      .matches(/^\d{5}$/, t("zipCode.zipCodeInvalid"))
      .notRequired(),
  });

  const YupValidationCountry = yup.string().when([], {
    is: () => addressRequired,
    then: yup.string().required(t("countryRequired")),
    otherwise: yup.string().notRequired(),
  });

  const YupValidationPhoneOptional = yup
    .string()
    .nullable()
    .matches(/^\+?\d+$/, t("phoneNumber.phoneNumberInvalid"));

  const YupValidationPhone = yup
    .string()
    .required(t("phoneNumber.phoneNumberRequired"))
    .matches(/^\+?\d+$/, t("phoneNumber.phoneNumberInvalid"));

  const YupValidationOrganizationName = yup.string().required(t("organizationRequired"));

  const YupValidationOrganizationType = yup
    .string()
    .required(t("organizationTypeRequired"));

  const YupValidationOrganizationTaxNumber = yup
    .string()
    .required(t("taxNumberRequired"));

  const YupValidationOrganizationTaxNumberSelected = (
    referenceField: string,
    referenceField2: string
  ) =>
    yup.string().test(referenceField, t("taxNumberRequired"), function () {
      const ref = yup.ref(referenceField);
      const ref2 = yup.ref(referenceField2);
      const resolveRef = this.resolve(ref);
      const resolveRef2 = this.resolve(ref2);

      if (resolveRef === undefined && resolveRef2 === undefined) {
        return false;
      }
      return true;
    });

  const YupValidationGroupDocumentDateSelected = (
    referenceField: string,
    referenceField2: string
  ) =>
    yup
      .date()
      .nullable()
      .when(referenceField2, {
        is: false,
        then: yup
          .date()
          .typeError(t("dates.dueDateInvalid"))
          .min(
            new Date(new Date().setDate(new Date().getDate() - 1)),
            t("dates.dateFuture")
          )
          .required(t("expiryDateValueRequired")),
        otherwise: yup
          .date()
          .nullable()
          .typeError(t("dates.dueDateInvalid"))
          .min(
            new Date(new Date().setDate(new Date().getDate() - 1)),
            t("dates.dateFuture")
          )
          .notRequired(),
      });

  const YupValidationOrganizationCommercialRegisterNumber = yup
    .string()
    .required(t("commercialRegisterNumberRequired"));

  const YupValidationTaskDescription = yup.string().required(t("organizationRequired"));

  const YupValidationTaskDeadline = yup.number().min(0, t("dates.deadlineInvalid"));

  const YupValidationOrganizationID = yup
    .string()
    .required(t("organizationRequired"))
    .test("organizationIDnotNIL", t("organizationRequired"), function (organizationID) {
      return organizationID !== NIL_UUID;
    });

  const YupValidationMessage = yup.string().required(t("messageRequired"));

  const YupValidationTopic = yup.string().required(t("topicRequired"));

  const YupValidationSalutation = yup.string().required(t("salutationRequired"));

  const YupValidationGroupMembershipDocumentType = (
    groupMembershipDocumentTypeOptions: any
  ) =>
    yup.string().oneOf(
      groupMembershipDocumentTypeOptions.map((documentTypeOption: any) => {
        return documentTypeOption.value;
      }),
      t("invalidSelection", { ns: "validations" })
    );

  const YupValidationCertificateVerification = (requiredLength: number) => {
    return yup
      .string()
      .length(
        requiredLength,
        t("certificateVerification.requiredLength", { length: requiredLength })
      )
      .required(t("certificateVerification.required"));
  };

  return {
    YupValidationFirstName,
    YupValidationMatchFirstName,
    YupValidationLastName,
    YupValidationMatchLastName,
    YupValidationEmail,
    YupValidationEmailOrUsername,
    YupValidationConfirmEmail,
    YupValidationBirthday,
    YupValidationMatchBirthday,
    YupValidationDueDate,
    YupValidationStreet,
    YupValidationStreetNumber,
    YupValidationCity,
    YupValidationZipCode,
    YupValidationCountry,
    YupValidationPhoneOptional,
    YupValidationPhone,
    YupValidationOrganizationName,
    YupValidationOrganizationTaxNumber,
    YupValidationOrganizationCommercialRegisterNumber,
    YupValidationOrganizationTaxNumberSelected,
    YupValidationOrganizationType,
    YupValidationTaskDescription,
    YupValidationTaskDeadline,
    YupValidationOrganizationID,
    YupValidationMessage,
    YupValidationTopic,
    YupValidationSalutation,
    YupValidationGuardianEmail,
    YupValidationConfirmGuardianEmail,
    YupValidationGroupDocumentDateSelected,
    YupValidationGroupMembershipDocumentType,
    YupValidationCertificateVerification,
    YupValidation2FaCode,
  };
};
